/**
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import { Injectable } from '@angular/core';
import { ElFontIconPackParams, ElIconPack, ElIconPackParams, ElIconPackType, ElIcons } from './icon-pack';
import { ElFontIcon, ElIcon, ElSvgIcon } from './icon';

export class ElIconDefinition {
  name: string;
  type: string;
  pack: string;
  icon: ElIcon;
}

function throwPackNotFoundError(name: string) {
  throw Error(`Icon Pack '${name}' is not registered`);
}

function throwNoDefaultPackError() {
  throw Error('Default pack is not registered.');
}

function throwIconNotFoundError(name: string, pack: string) {
  throw Error(`Icon '${name}' is not registered in pack '${pack}'. Check icon name or consider switching icon pack.`);
}

function throwWrongPackTypeError(name: string, type: string, desiredType: string) {
  throw Error(`Pack '${name}' is not an '${desiredType}' Pack and its type is '${type}'`);
}

/**
 * This service allows to register multiple icon packs to use them later within `<el-icon></el-icon>` component.
 */
@Injectable({providedIn: 'root'})
export class ElIconLibraries {

  protected packs: Map<string, ElIconPack> = new Map();
  protected defaultPack: ElIconPack;

  /**
   * Registers new Svg icon pack
   * @param {string} name
   * @param {ElIcon} icons
   * @param {ElIconPackParams} params
   */
  registerSvgPack(name: string, icons: ElIcons, params: ElIconPackParams= {}) {
    this.packs.set(name, {
      name,
      icons: new Map(Object.entries(icons)),
      params,
      type: ElIconPackType.SVG,
    });
  }

  /**
   * Registers new font pack
   * @param {string} name
   * @param {ElIconPackParams} params
   */
  registerFontPack(name: string, params: ElFontIconPackParams = {}) {
    this.packs.set(name, {
      name,
      params,
      icons: new Map(),
      type: ElIconPackType.FONT,
    });
  }

  /**
   * Returns pack by name
   * @param {string} name
   */
  getPack(name: string): ElIconPack {
    return this.packs.get(name);
  }

  /**
   * Sets pack as a default
   * @param {string} name
   */
  setDefaultPack(name: string) {
    if (!this.packs.has(name)) {
      throwPackNotFoundError(name);
    }

    this.defaultPack = this.packs.get(name);
  }

  /**
   * Returns Svg icon
   * @param {string} name
   * @param {string} pack
   *
   * @returns ElIconDefinition
   */
  getSvgIcon(name: string, pack?: string): ElIconDefinition {
    const iconsPack = pack ? this.getPackOrThrow(pack) : this.getDefaultPackOrThrow();

    if (iconsPack.type !== ElIconPackType.SVG) {
      throwWrongPackTypeError(iconsPack.name, iconsPack.type, 'SVG');
    }

    const icon = this.getIconFromPack(name, iconsPack);

    return {
      name,
      pack: iconsPack.name,
      type: ElIconPackType.SVG,
      icon: this.createSvgIcon(name, icon, iconsPack.params),
    };
  }

  /**
   * Returns Font icon
   * @param {string} name
   * @param {string} pack
   *
   * @returns ElIconDefinition
   */
  getFontIcon(name: string, pack?: string): ElIconDefinition {
    const iconsPack = pack ? this.getPackOrThrow(pack) : this.getDefaultPackOrThrow();

    if (iconsPack.type !== ElIconPackType.FONT) {
      throwWrongPackTypeError(iconsPack.name, iconsPack.type, 'Font');
    }

    const icon = this.getIconFromPack(name, iconsPack, false);

    return {
      name,
      pack: iconsPack.name,
      type: ElIconPackType.FONT,
      icon: this.createFontIcon(name, icon ? icon : '', iconsPack.params),
    };
  }

  /**
   * Returns an icon
   * @param {string} name
   * @param {string} pack
   *
   * @returns ElIconDefinition
   */
  getIcon(name: string, pack?: string): ElIconDefinition {
    const iconsPack = pack ? this.getPackOrThrow(pack) : this.getDefaultPackOrThrow();

    if (iconsPack.type === ElIconPackType.SVG) {
      return this.getSvgIcon(name, pack);
    }

    return this.getFontIcon(name, pack);
  }

  protected createSvgIcon(name: string, content: ElIcon | string, params: ElIconPackParams): ElSvgIcon {
    return content instanceof ElSvgIcon ? content : new ElSvgIcon(name, content, params);
  }

  protected createFontIcon(name: string, content: ElIcon | string, params: ElFontIconPackParams): ElFontIcon {
    return content instanceof ElFontIcon ? content : new ElFontIcon(name, content, params);
  }

  protected getPackOrThrow(name: string): ElIconPack {

    const pack: ElIconPack = this.packs.get(name);
    if (!pack) {
      throwPackNotFoundError(name);
    }
    return pack;
  }

  protected getDefaultPackOrThrow(): ElIconPack {

    if (!this.defaultPack) {
      throwNoDefaultPackError();
    }
    return this.defaultPack;
  }

  protected getIconFromPack(name: string, pack: ElIconPack, shouldThrow = true): ElIcon | string {
    if (shouldThrow && !pack.icons.has(name)) {
      throwIconNotFoundError(name, pack.name);
    }

    return pack.icons.get(name);
  }
}
